/**
 * Produktfinder – Shopify Section Controller
 *
 * Steuert den Fragenfluss (Iteration A) und zeigt passende Ergebnisse inline.
 * Architektur analog zum Webapp-Lead-Funnel (useReducer + History), aber als
 * Vanilla-Klasse für mehrere Section-Instanzen auf einer Seite.
 *
 * Fragenfluss (1 Raum):
 *   Q1 Räume → Q2 Größe → Q3 Montage → Q4 Technologie → Ergebnis
 *
 * Kurzpfade:
 *   2 Räume → Duo-Split (LP14) | 3 Räume → Trio-Split (LP15)
 *   4+ Räume oder >90 m² → Beratungs-Lead (LP13)
 *
 * Sonderregel: DIY + ≤45 m² → Quick Connect nur 12k (LP1/LP2), Hinweis auf Ergebnis.
 *
 * Iteration B (Mehrraum mit Montage/Tech): nur computeResultPath() erweitern.
 *
 * @see Kältebringer_Produktfinder Konzept.docx.md
 */
(function () {
  "use strict";

  // ===========================================================================
  // Konstanten – Ergebnispfad-IDs (15 Pfade, Iteration A)
  // ===========================================================================

  /**
   * @typedef {'lp1_qc_12k_r32'|'lp2_qc_12k_r290'|'lp3_mono_9k_r32'|'lp4_mono_9k_r290'|'lp5_qc_12k_r32'|'lp6_qc_12k_r290'|'lp7_mono_12k_r32'|'lp8_mono_12k_r290'|'lp9_qc_18k_r32'|'lp10_qc_18k_r290'|'lp11_mono_18k_r32'|'lp12_mono_18k_r290'|'lp13_consultation'|'lp14_duo_split'|'lp15_trio_split'|null} ResultPathId
   */

  /** Pfade, bei denen QC R290 noch nicht live ist → Lead-Fallback wenn Toggle aus. */
  const QC_R290_PATHS = new Set([
    "lp2_qc_12k_r290",
    "lp6_qc_12k_r290",
    "lp10_qc_18k_r290",
  ]);

  const PHONE_RE = /^\+[1-9]\d{1,14}$/;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // ===========================================================================
  // Pfad-Berechnung (isoliert für Iteration B)
  // ===========================================================================

  /**
   * Ermittelt den Ergebnispfad aus den Nutzerantworten.
   *
   * | Pfad | Räume | Größe   | Montage | Tech |
   * |------|-------|---------|---------|------|
   * | lp1  | 1     | ≤45     | DIY     | R32  | (QC 12k, hochgestuft)
   * | lp2  | 1     | ≤45     | DIY     | R290 |
   * | lp3  | 1     | ≤45     | Pro     | R32  |
   * | lp4  | 1     | ≤45     | Pro     | R290 |
   * | lp5–12 | 1   | 46–90   | …       | …    |
   * | lp13 | 1 >90 oder 4+ | — | — |
   * | lp14 | 2     | —       | —       | —    |
   * | lp15 | 3     | —       | —       | —    |
   *
   * @param {{ rooms?: string, size?: string, install?: string, tech?: string }} answers
   * @returns {ResultPathId}
   */
  function computeResultPath(answers) {
    const { rooms, size, install, tech } = answers;

    if (rooms === "4plus") return "lp13_consultation";
    if (rooms === "2") return "lp14_duo_split";
    if (rooms === "3") return "lp15_trio_split";

    if (rooms !== "1") return null;
    if (size === "gt90") return "lp13_consultation";

    if (size === "lt45") {
      if (install === "diy" && tech === "r32") return "lp1_qc_12k_r32";
      if (install === "diy" && tech === "r290") return "lp2_qc_12k_r290";
      if (install === "pro" && tech === "r32") return "lp3_mono_9k_r32";
      if (install === "pro" && tech === "r290") return "lp4_mono_9k_r290";
    }
    if (size === "s45_60") {
      if (install === "diy" && tech === "r32") return "lp5_qc_12k_r32";
      if (install === "diy" && tech === "r290") return "lp6_qc_12k_r290";
      if (install === "pro" && tech === "r32") return "lp7_mono_12k_r32";
      if (install === "pro" && tech === "r290") return "lp8_mono_12k_r290";
    }
    if (size === "s60_90") {
      if (install === "diy" && tech === "r32") return "lp9_qc_18k_r32";
      if (install === "diy" && tech === "r290") return "lp10_qc_18k_r290";
      if (install === "pro" && tech === "r32") return "lp11_mono_18k_r32";
      if (install === "pro" && tech === "r290") return "lp12_mono_18k_r290";
    }

    return null;
  }

  /**
   * Nächste View nach einer Antwort (Branching).
   * @param {string} currentView
   * @param {{ rooms?: string, size?: string, install?: string, tech?: string }} answers
   * @returns {string}
   */
  function getNextView(currentView, answers) {
    if (currentView === "landing") return "q1_rooms";
    if (currentView === "q1_rooms") {
      if (answers.rooms === "1") return "q2_size";
      if (answers.rooms === "2" || answers.rooms === "3") return "result";
      if (answers.rooms === "4plus") return "lead";
    }
    if (currentView === "q2_size") {
      if (answers.size === "gt90") return "lead";
      return "q3_install";
    }
    if (currentView === "q3_install") return "q4_tech";
    if (currentView === "q4_tech") return "result";
    return "landing";
  }

  /**
   * Fortschrittsanzeige (0–100) und Schritt-Label.
   * @param {string} view
   * @param {{ rooms?: string }} answers
   */
  function getProgress(view, answers) {
    const map = {
      landing: { pct: 0, step: 0, total: 4 },
      q1_rooms: { pct: 25, step: 1, total: 4 },
      q2_size: { pct: 50, step: 2, total: 4 },
      q3_install: { pct: 75, step: 3, total: 4 },
      q4_tech: { pct: 88, step: 4, total: 4 },
      result: { pct: 100, step: 4, total: 4 },
      lead: { pct: 100, step: 4, total: 4 },
    };
    if (answers.rooms === "2" || answers.rooms === "3") {
      if (view === "q1_rooms") return { pct: 50, step: 1, total: 1 };
      if (view === "result") return { pct: 100, step: 1, total: 1 };
    }
    if (answers.rooms === "4plus") {
      if (view === "q1_rooms") return { pct: 50, step: 1, total: 1 };
      if (view === "lead") return { pct: 100, step: 1, total: 1 };
    }
    return map[view] || map.landing;
  }

  // ===========================================================================
  // URL-Sync
  // ===========================================================================

  function buildSearchParams(state) {
    const p = new URLSearchParams();
    if (state.view && state.view !== "landing") p.set("step", state.view);
    if (state.answers.rooms) p.set("rooms", state.answers.rooms);
    if (state.answers.size) p.set("size", state.answers.size);
    if (state.answers.install) p.set("install", state.answers.install);
    if (state.answers.tech) p.set("tech", state.answers.tech);
    if (state.resultPathId) p.set("result", state.resultPathId);
    return p;
  }

  function parseUrlToState() {
    const p = new URLSearchParams(window.location.search);
    const step = p.get("step") || "landing";
    const answers = {
      rooms: p.get("rooms") || undefined,
      size: p.get("size") || undefined,
      install: p.get("install") || undefined,
      tech: p.get("tech") || undefined,
    };
    let resultPathId = p.get("result") || computeResultPath(answers);
    let view = step;

    if (!p.get("step") && resultPathId) {
      view = "result";
    }
    if (view === "q4_tech" && answers.tech) {
      view = "result";
    }
    if ((view === "result" || view === "lead") && !resultPathId) {
      resultPathId = computeResultPath(answers);
    }
    if (view === "q3_install" && answers.install && answers.rooms === "1" && answers.size && answers.size !== "gt90") {
      /* stay on q3 until tech chosen */
    }

    return {
      view,
      history: [],
      answers,
      resultPathId: resultPathId || null,
    };
  }

  // ===========================================================================
  // ProductFinder – eine Instanz pro Section
  // ===========================================================================

  class ProductFinder {
    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || "";
      this.apiBaseUrl = (root.dataset.apiBaseUrl || "").replace(/\/+$/, "");
      this.qcR290Available = root.dataset.qcR290Available === "true";
      this.privacyUrl = root.dataset.privacyUrl || "/policies/privacy-policy";

      this.state = {
        view: "landing",
        history: [],
        answers: {},
        resultPathId: null,
      };

      this._views = root.querySelectorAll("[data-kpf-view]");
      this._backBtn = root.querySelector("[data-kpf-back]");
      this._progressBar = root.querySelector("[data-kpf-progress-bar]");
      this._stepMeta = root.querySelector("[data-kpf-step-meta]");
      this._shell = root.querySelector("[data-kpf-shell]");
      this._resultPanels = root.querySelectorAll("[data-kpf-result-panel]");
      this._leadWrap = root.querySelector("[data-kpf-lead-wrap]");
      this._leadForm = root.querySelector("[data-kpf-lead-form]");
      this._leadSuccess = root.querySelector("[data-kpf-lead-success]");
      this._unavailable = root.querySelector("[data-kpf-unavailable]");
      this._restartBtn = root.querySelector("[data-kpf-restart]");

      this._popstateBound = this._onPopState.bind(this);
      this._bindEvents();

      const hydrated = parseUrlToState();
      if (hydrated.view !== "landing" && hydrated.view) {
        this.state = hydrated;
        if (!this.state.resultPathId) {
          this.state.resultPathId = computeResultPath(this.state.answers);
        }
        if (
          this.state.resultPathId &&
          this._shouldUseLeadView(this.state.resultPathId)
        ) {
          this.state.view = "lead";
        } else if (this.state.view === "lead" && this.state.resultPathId) {
          this.state.view = "result";
        }
      }

      this._render(false);
    }

    /** Event-Delegation für Optionen, Navigation, Lead-Submit. */
    _bindEvents() {
      this.root.addEventListener("click", (e) => {
        const opt = e.target.closest("[data-kpf-option]");
        if (opt) {
          e.preventDefault();
          this._onOptionSelect(opt);
          return;
        }
        if (e.target.closest("[data-kpf-start]")) {
          e.preventDefault();
          this._goTo("q1_rooms", true);
          return;
        }
        if (e.target.closest("[data-kpf-back]")) {
          e.preventDefault();
          this._goBack();
          return;
        }
        if (e.target.closest("[data-kpf-restart]")) {
          e.preventDefault();
          this._restart();
        }
      });

      if (this._leadForm) {
        this._leadForm.addEventListener("submit", (e) => {
          e.preventDefault();
          this._submitLead();
        });
      }

      window.addEventListener("popstate", this._popstateBound);
    }

    _onPopState() {
      const hydrated = parseUrlToState();
      this.state.view = hydrated.view;
      this.state.answers = hydrated.answers;
      this.state.resultPathId = hydrated.resultPathId;
      this._render(false);
    }

    /**
     * Antwort gewählt → State aktualisieren, ggf. Ergebnis berechnen, weiter.
     * @param {HTMLButtonElement} button
     */
    _onOptionSelect(button) {
      const view = this.state.view;
      const value = button.dataset.kpfOptionValue;
      if (!value) return;

      const keyMap = {
        q1_rooms: "rooms",
        q2_size: "size",
        q3_install: "install",
        q4_tech: "tech",
      };
      const key = keyMap[view];
      if (!key) return;

      this.state.answers[key] = value;

      const next = getNextView(view, this.state.answers);
      if (next === "result") {
        this.state.resultPathId = computeResultPath(this.state.answers);
        const useLead = this._shouldUseLeadView(this.state.resultPathId);
        this._goTo(useLead ? "lead" : "result", true);
        return;
      }
      if (next === "lead") {
        this.state.resultPathId =
          this.state.answers.rooms === "4plus" || this.state.answers.size === "gt90"
            ? "lp13_consultation"
            : computeResultPath(this.state.answers);
        this._goTo("lead", true);
        return;
      }
      this._goTo(next, true);
    }

    /**
     * QC-R290-Pfade oder Beratung → Lead-View statt Produkt-Ergebnis.
     * @param {ResultPathId} pathId
     */
    _shouldUseLeadView(pathId) {
      if (pathId === "lp13_consultation") return true;
      if (!this.qcR290Available && pathId && QC_R290_PATHS.has(pathId)) return true;
      return false;
    }

    /**
     * @param {string} view
     * @param {boolean} pushHistory
     */
    _goTo(view, pushHistory) {
      if (pushHistory) {
        this.state.history.push(this.state.view);
      }
      this.state.view = view;
      if (view === "result" || view === "lead") {
        if (!this.state.resultPathId) {
          this.state.resultPathId = computeResultPath(this.state.answers);
        }
      }
      this._render(true);
    }

    _goBack() {
      if (this.state.history.length === 0) {
        this._restart();
        return;
      }
      const prev = this.state.history.pop();
      this.state.view = prev;
      if (prev !== "result" && prev !== "lead") {
        this.state.resultPathId = null;
      }
      this._render(true);
    }

    _restart() {
      this.state = {
        view: "landing",
        history: [],
        answers: {},
        resultPathId: null,
      };
      if (this._leadForm) {
        this._leadForm.reset();
        this._leadForm.removeAttribute("data-kpf-lead-hidden");
      }
      if (this._leadSuccess) this._leadSuccess.hidden = true;
      this._clearLeadErrors();
      this._render(true);
    }

    /**
     * UI aktualisieren und URL synchronisieren.
     * @param {boolean} pushUrl
     */
    _render(pushUrl) {
      const { view, answers, resultPathId } = this.state;

      this._views.forEach((el) => {
        const id = el.dataset.kpfView;
        el.hidden = id !== view;
        el.classList.toggle("kpf__view--enter", id === view);
      });

      const showShell = view !== "landing";
      if (this._shell) this._shell.hidden = !showShell;

      const prog = getProgress(view, answers);
      if (this._progressBar) this._progressBar.style.width = prog.pct + "%";
      if (this._stepMeta) {
        if (view === "landing" || view === "result" || view === "lead") {
          this._stepMeta.textContent = "";
        } else if (prog.total > 0 && prog.step > 0) {
          this._stepMeta.textContent = "Schritt " + prog.step + " von " + prog.total;
        }
      }

      if (this._backBtn) {
        this._backBtn.hidden = view === "landing";
      }

      if (view === "result") {
        this._showResultPanel(resultPathId);
        if (this._leadWrap) this._leadWrap.hidden = true;
      } else if (view === "lead") {
        this._hideAllResultPanels();
        if (this._leadWrap) this._leadWrap.hidden = false;
        this._updateLeadContext(resultPathId);
      } else {
        this._hideAllResultPanels();
        if (this._leadWrap) this._leadWrap.hidden = true;
      }

      if (pushUrl) {
        const params = buildSearchParams(this.state);
        const qs = params.toString();
        const url =
          window.location.pathname +
          (qs ? "?" + qs : "") +
          window.location.hash;
        history.pushState({ kpf: this.sectionId }, "", url);
      }

      this.root.scrollIntoView({ behavior: "smooth", block: "start" });

      // Slot für spätere Analytics (GTM dataLayer)
      if (typeof window.dataLayer !== "undefined" && (view === "result" || view === "lead")) {
        window.dataLayer.push({
          event: "product_finder_complete",
          result_path: resultPathId,
          rooms: answers.rooms,
        });
      }
    }

    _hideAllResultPanels() {
      this._resultPanels.forEach((p) => {
        p.hidden = true;
      });
    }

    /**
     * Zeigt genau ein vorgerendertes Ergebnis-Panel.
     * @param {ResultPathId} pathId
     */
    _showResultPanel(pathId) {
      this._hideAllResultPanels();
      if (!pathId) return;
      const panel = this.root.querySelector(
        '[data-kpf-result-id="' + pathId + '"]',
      );
      if (panel) panel.hidden = false;
    }

    /**
     * Lead-Kontext: Beratung vs. „Bald verfügbar“ (R290 QC).
     * @param {ResultPathId} pathId
     */
    _updateLeadContext(pathId) {
      if (this._unavailable) {
        const showUnavailable =
          pathId && QC_R290_PATHS.has(pathId) && !this.qcR290Available;
        this._unavailable.hidden = !showUnavailable;
      }
      const title = this.root.querySelector("[data-kpf-lead-title]");
      const desc = this.root.querySelector("[data-kpf-lead-desc]");
      if (pathId === "lp13_consultation") {
        if (title) title.textContent = this.root.dataset.leadTitleConsult || title.textContent;
        if (desc) desc.textContent = this.root.dataset.leadDescConsult || desc.textContent;
      } else if (pathId && QC_R290_PATHS.has(pathId)) {
        if (title) title.textContent = this.root.dataset.leadTitleUnavailable || title.textContent;
        if (desc) desc.textContent = this.root.dataset.leadDescUnavailable || desc.textContent;
      }
    }

    _clearLeadErrors() {
      this.root.querySelectorAll("[data-kpf-lead-error]").forEach((el) => {
        el.textContent = "";
      });
      const banner = this.root.querySelector("[data-kpf-lead-banner]");
      if (banner) banner.hidden = true;
    }

    /**
     * Lead an submit-product-finder-lead senden.
     */
    async _submitLead() {
      if (!this._leadForm || !this.apiBaseUrl) return;

      this._clearLeadErrors();
      const fd = new FormData(this._leadForm);
      const payload = {
        firstName: String(fd.get("firstName") || "").trim(),
        lastName: String(fd.get("lastName") || "").trim(),
        email: String(fd.get("email") || "").trim(),
        phone: String(fd.get("phone") || "").trim(),
        message: String(fd.get("message") || "").trim() || undefined,
        dataConsent: fd.get("dataConsent") === "1",
        resultPathId: this.state.resultPathId || "lp13_consultation",
        productFinderAnswers: { ...this.state.answers },
      };

      const fieldErrors = this._validateLead(payload);
      if (Object.keys(fieldErrors).length > 0) {
        Object.entries(fieldErrors).forEach(([field, msg]) => {
          const el = this.root.querySelector('[data-kpf-lead-error="' + field + '"]');
          if (el) el.textContent = msg;
        });
        return;
      }

      const submitBtn = this._leadForm.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      const banner = this.root.querySelector("[data-kpf-lead-banner]");
      try {
        const res = await fetch(
          this.apiBaseUrl + "/submit-product-finder-lead",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          if (data.errors && Array.isArray(data.errors)) {
            data.errors.forEach((err) => {
              const el = this.root.querySelector(
                '[data-kpf-lead-error="' + err.field + '"]',
              );
              if (el) el.textContent = err.message;
            });
          } else if (banner) {
            banner.hidden = false;
            banner.dataset.type = "error";
            banner.textContent =
              data.message ||
              data.error ||
              (typeof data === "string" ? data : "Anfrage konnte nicht gesendet werden.");
          }
          return;
        }

        this._leadForm.setAttribute("data-kpf-lead-hidden", "");
        if (this._leadSuccess) {
          this._leadSuccess.hidden = false;
          const refEl = this.root.querySelector("[data-kpf-lead-success-ref]");
          if (refEl && data.referenceId) {
            refEl.hidden = false;
            refEl.textContent = "Referenz: " + data.referenceId;
          }
        }
        if (banner) banner.hidden = true;
      } catch (_err) {
        if (banner) {
          banner.hidden = false;
          banner.dataset.type = "error";
          banner.textContent =
            "Netzwerkfehler. Bitte versuchen Sie es später erneut.";
        }
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    }

    /**
     * @param {object} payload
     * @returns {Record<string, string>}
     */
    _validateLead(payload) {
      const err = {};
      if (!payload.firstName) err.firstName = "Bitte Vornamen eingeben.";
      if (!payload.lastName) err.lastName = "Bitte Nachnamen eingeben.";
      if (!payload.email || !EMAIL_RE.test(payload.email)) {
        err.email = "Bitte gültige E-Mail-Adresse eingeben.";
      }
      if (!payload.phone || !PHONE_RE.test(payload.phone)) {
        err.phone =
          "Bitte Telefon im Format +491701234567 eingeben (mit Ländervorwahl).";
      }
      if (!payload.dataConsent) {
        err.dataConsent = "Bitte stimmen Sie der Datenschutzerklärung zu.";
      }
      return err;
    }

    destroy() {
      window.removeEventListener("popstate", this._popstateBound);
    }
  }

  // ===========================================================================
  // Initialisierung (mehrere Sections, Theme-Editor)
  // ===========================================================================

  const instances = new WeakMap();

  function initAll(container) {
    const scope = container || document;
    scope.querySelectorAll("[data-kpf]:not([data-kpf-inited='1'])").forEach((root) => {
      root.setAttribute("data-kpf-inited", "1");
      instances.set(root, new ProductFinder(root));
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (ev) => {
    initAll(ev.target);
  });

  document.addEventListener("shopify:section:unload", (ev) => {
    ev.target.querySelectorAll("[data-kpf]").forEach((root) => {
      const inst = instances.get(root);
      if (inst) inst.destroy();
    });
  });
})();
